import { useState, useEffect } from 'react';

const DEFAULT_COLORS = [
  '#808080', '#f59e0b', '#f97316', '#ef4444', '#dc2626',
  '#be185d', '#22c55e', '#059669', '#0891b2', '#2563eb', '#7c3aed', '#a855f7',
];

const DEFAULT_RETRACE = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
const DEFAULT_EXT = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.272, 1.618, 2, 2.618, 4.236];

/**
 * Fib levels editor panel.
 * Manages its own local copy of levels and pushes changes up via onChange.
 */
export default function FibLevelsEditor({ initialLevels, onChange, onClose }) {
  const [levels, setLevels] = useState(() => initialLevels || []);
  const [newLevel, setNewLevel] = useState('');

  // Sync when the selected drawing changes
  useEffect(() => {
    if (initialLevels) setLevels(initialLevels);
  }, [initialLevels]);

  const push = (updated) => {
    setLevels(updated);
    onChange(updated);
  };

  const updateLevel = (index, key, value) => {
    push(levels.map((l, i) => i === index ? { ...l, [key]: value } : l));
  };

  const removeLevel = (index) => {
    push(levels.filter((_, i) => i !== index));
  };

  const addLevel = () => {
    const val = parseFloat(newLevel);
    if (isNaN(val)) return;
    const ratio = val / 100;
    if (levels.some((l) => Math.abs(l.level - ratio) < 0.0001)) return;
    const color = DEFAULT_COLORS[levels.length % DEFAULT_COLORS.length];
    push([...levels, { level: ratio, color, visible: true }].sort((a, b) => a.level - b.level));
    setNewLevel('');
  };

  const resetDefaults = () => {
    const hasExtension = levels.some((l) => l.level > 1.01);
    const defaults = hasExtension ? DEFAULT_EXT : DEFAULT_RETRACE;
    push(defaults.map((level, i) => ({
      level,
      color: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
      visible: true,
    })));
  };

  return (
    <div className="fib-editor" onClick={(e) => e.stopPropagation()}>
      <div className="fib-editor-header">
        <span className="fib-editor-title">Fibonacci Levels</span>
        <div className="fib-editor-actions">
          <button className="fib-editor-reset" onClick={resetDefaults} title="Reset defaults">
            Reset
          </button>
          <button className="fib-editor-close" onClick={onClose} title="Close">
            <svg width="10" height="10" viewBox="0 0 10 10"><path d="M8 2L2 8M2 2l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>
      </div>

      <div className="fib-editor-list">
        {levels.map((l, i) => (
          <div key={`${l.level}-${i}`} className={`fib-level-row ${l.visible === false ? 'fib-level-hidden' : ''}`}>
            <button
              className="fib-level-vis"
              onClick={() => updateLevel(i, 'visible', l.visible === false ? true : false)}
              title={l.visible !== false ? 'Hide level' : 'Show level'}
            >
              {l.visible !== false ? '●' : '○'}
            </button>
            <span className="fib-level-pct">{(l.level * 100).toFixed(1)}%</span>
            <input
              type="color"
              value={l.color || '#f59e0b'}
              onChange={(e) => updateLevel(i, 'color', e.target.value)}
              className="fib-level-color"
              title="Level color"
            />
            <button className="fib-level-del" onClick={() => removeLevel(i)} title="Remove level">
              <svg width="10" height="10" viewBox="0 0 10 10"><path d="M8 2L2 8M2 2l6 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
            </button>
          </div>
        ))}
      </div>

      <div className="fib-editor-add">
        <input
          type="number"
          className="fib-add-input"
          placeholder="New level %"
          value={newLevel}
          onChange={(e) => setNewLevel(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addLevel()}
          step="0.1"
        />
        <button className="fib-add-btn" onClick={addLevel} disabled={!newLevel}>
          Add
        </button>
      </div>
    </div>
  );
}
